import _ from 'lodash';
import { useEffect, useState, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import moment from 'moment';

//Components
import { Button, Modal, ModalBody, ModalFooter, Select, Tooltip, TableView } from "@gpstrackit/react-controls";
import { Tabs, TabContainer } from "@gpstrackit/react-controls";
import CustomCard from 'Components/Card';
import { Alert } from '@mui/material';
import Icon from '@mui/material/Icon';
import InputDate from 'Components/InputDate';

import { SearchInput } from "@gpstrackit/gtc-react-controls";

//Helpers
import { getDateFormatted, getDateRange } from 'Core/data/Helpers';

//Views
import ConfirmResolveMaintenanceWarningEvent from 'Modules/devices/.components/maintenanceWarnings/.components/confirmResolveMaintenanceWarningEvent';
import ResolveMaintenanceWarningsEvent from 'Modules/devices/.components/maintenanceWarnings/.components/resolveMaintenanceWarningEvent';
import DeleteMaintenanceWarningEvent from 'Modules/devices/.components/maintenanceWarnings/.components/deleteMaintenanceWarningEvent';
import DetailedList from 'Modules/devices/.components/maintenanceWarnings/.components/detailedList';

//Actions
import {
    openModalConfirmResolveMaintenanceWarningEvent,
    openModalDeleteMaintenanceWarningEvent,
    openModalResolveMaintenanceWarningEvent,
    openModalDetailedList
} from "Redux/actions";

import {
    maintenanceWarningsSearchGroupedMaintenanceWarningEvents,
    maintenanceWarningsSearchLanguageLabels,
    reset
} from './redux/slice'

//Styles
import './index.scss';

const IconStatus = ({ onClick, firstIcon, secondIcon }) => {
    const [isHovered, setIsHovered] = useState(false);

    const handleMouseEnter = () => {
        setIsHovered(true);
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
    };

    const iconStyle = {
        marginRight: 5,
        marginTop: -2,
        cursor: 'pointer',
    };

    const iconColor = firstIcon === 'delete' ? (isHovered ? '#F44336' : '#66BB6A') : (isHovered ? '#66BB6A' : '#F44336');

    return (
        <Icon
            style={{ ...iconStyle, color: iconColor }}
            onClick={() => onClick?.()}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {isHovered ? firstIcon : secondIcon}
        </Icon>
    );
};

const MaintenanceWarnings = (props) => {
    const { messages, locale } = useIntl();
    const dispatch = useDispatch();
    const { tableRef } = props
    const tableViewRef = useRef();
    //Variables
    const optionsTimeMetric = [
        { id: `thisweek`, name: messages['dashboard_gadget_thisweek'] },
        { id: `lastweek`, name: messages['dashboard_gadget_lastweek'] },
        { id: `thismonth`, name: messages['dashboard_gadget_this_month'] },
        { id: `lastmonth`, name: messages['dashboard_gadget_last_month'] },
        { id: `last7days`, name: messages['dashboard_gadget_last7days'] },
        { id: `last15days`, name: messages['dashboard_gadget_last15days'] },
        { id: `custom`, name: messages['dashboard_gadget_custom'] },
    ];

    //Redux states
    const resultResolveMaintenanceWarningEvent = useSelector(state => state.confirmResolveMaintenanceWarningEventsRedux.result);
    const openModalConfirmRevolve = useSelector(state => state.confirmResolveMaintenanceWarningEventsRedux.openModal);
    const resultDeleteMaintenanceWarningEvent = useSelector(state => state.maintenanceWarningsDeleteRedux?.result);
    const openModalViewDetailedList = useSelector(state => state.maintenanceWarningsDetailedListRedux.openModal);
    const openModalViewResolve = useSelector(state => state.maintenanceWarningsConfirmResolveRedux.openModal);
    const { items, total, loading, data, openModal, offset } = useSelector(state => state.maintenanceWarningsRedux);
    const openModalDelete = useSelector(state => state.maintenanceWarningsDeleteRedux.openModal)

    //Local States
    const [startDate, setStartDate] = useState(moment().day(0).startOf('day').toDate());
    const [endDate, setEndDate] = useState(moment.max(moment()).toDate());
    const [timeMetric, setTimeMetric] = useState(optionsTimeMetric[4]);
    const [openSearch, setOpenSearch] = useState(null);
    const [tab, setTab] = useState(0);
    const [page, setPage] = useState(0);
    const [search, setSearch] = useState(false)

    const timeFromDate = (date, language = 'en') => {
        const dateMoment = moment(date).locale(language);
        const timeElapsed = dateMoment.fromNow();
        return timeElapsed;
    }

    const closeModal = () => {
        dispatch(reset());
        setSearch(false);
        setPage(0);
    }

    const calculateDateRange = () => {
        let dateRange = {}
        if (timeMetric?.id !== "custom") {
            const range = getDateRange(timeMetric.id)
            let currentStartDate = moment(range?.startDate)
            let currentEndDate = moment(range?.endDate)
            dateRange = {
                start: currentStartDate,
                end: currentEndDate
            }
        } else {
            let currentStartDate = startDate ? moment(startDate) : null;
            let currentEndDate = endDate ? moment(endDate) : null;
            dateRange = {
                start: currentStartDate,
                end: currentEndDate
            }
        }

        return dateRange
    }

    const handleSearch = useCallback(({unitId, dateRange, offset = 0, resolved = null}) => {
        if(!dateRange?.start || !dateRange?.end) {
            //Start and end date are required
            return;
        }
        dispatch(maintenanceWarningsSearchGroupedMaintenanceWarningEvents({ 
            unitId, 
            resolved: resolved !== null ? resolved : (tab === 0 ? false : true), 
            openSearch: openSearch || '', 
            dateRange, 
            offset 
        }))
    }, [tab, openSearch])

    const handleTabs = (newTab) => {
        setTab(newTab)
        dispatch(reset({ items: [], total: 0, offset: 0 }));
        let dateRange = calculateDateRange();
        if (newTab === 0) {
            handleSearch({ unitId: data?.id, resolved: false, dateRange })
        }
        if (newTab === 1) {
            handleSearch({ unitId: data?.id, resolved: true, dateRange })
        }
    }

    const onChangeStartEndDate = ({ start, end }) => {
        const newStart = start && moment(start).startOf('day').toDate();
        const newEnd = end && moment(end).endOf('day').toDate();
        setStartDate(newStart || null);
        setEndDate(newEnd || null);
    }

    const onChange = (value) => {
        const { start, end } = value;
        onChangeStartEndDate({
            start,
            end
        })
    };

    const onChangePage = (paramPage) => {
        setSearch(true)
        let currentOffset = paramPage === 1 ? 0 : offset + 10
        dispatch(reset({ offset: currentOffset }))
        let dateRange = calculateDateRange();

        handleSearch({ unitId: data?.id, dateRange, offset: currentOffset })
    }

    useEffect(() => {
        if (search) {
            dispatch(reset({ items: [], total: 0, offset: 0 }))
            let dateRange = calculateDateRange()

            handleSearch({ unitId: data?.id, resolved: tab === 0 ? false : true, dateRange })
        }
    }, [timeMetric, startDate, endDate, openModal])

    useEffect(() => {
        dispatch(maintenanceWarningsSearchLanguageLabels())
    }, [locale, openModal])

    useEffect(() => {
        if (resultResolveMaintenanceWarningEvent?.resolve || resultDeleteMaintenanceWarningEvent?.delete) {
            dispatch(reset({ items: [], total: 0, offset: 0 }))
            let dateRange = calculateDateRange()

            handleSearch({ unitId: data?.id, resolved: tab === 0 ? false : true, dateRange })
        }
        if (resultResolveMaintenanceWarningEvent?.resolve) {
            tableRef && tableRef?.current?.getData();
        }
    }, [resultResolveMaintenanceWarningEvent, resultDeleteMaintenanceWarningEvent])

    let sdCard = data?.sdCard ? JSON.parse(data?.sdCard) : null

    const generateCard = () => {
        return (
            <div style={{ marginBottom: 30 }}>
                {
                    items?.length > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'end', margin: '10px 0' }}>
                            <a onClick={() => {
                                if (tab) {
                                    let dateRange = calculateDateRange()
                                    dispatch(openModalDeleteMaintenanceWarningEvent({ unitId: data?.id, unitLabel: data?.label, dateRange: dateRange, bulk: true }))
                                } else {
                                    let dateRange = calculateDateRange()
                                    dispatch(openModalConfirmResolveMaintenanceWarningEvent({ unitId: data?.id, unitLabel: data?.label, dateRange: dateRange, bulk: true }))
                                }

                            }} style={{ textDecoration: 'underline', cursor: 'pointer' }}>{!tab ? messages['maintenance_warnings_tab_resolve_all'] : messages['maintenance_warnings_tab_clear_all']}</a>
                        </div>
                    )
                }
                <div style={{ marginTop: (!items?.length || !tab) ? 10 : 0 }}>
                    <div className="maintenance-warnings-input-search" style={{ marginBottom: 5 }}>
                        <SearchInput
                            searchText={openSearch}
                            onSearchText={(value) => setOpenSearch(value)}
                            icon="search"
                            placeholder={messages['maintenance_warnings_tab_search_by_warning']}
                        />
                    </div>
                    <div style={{ marginBottom: 15 }}>
                        <Select
                            id="timeMetric"
                            placeholder={messages['dashboard_gadget_time_metric']}
                            options={optionsTimeMetric}
                            textField="name"
                            valueField="id"
                            isSearchable={false}
                            value={timeMetric}
                            onChange={(value) => setTimeMetric(value)}
                            required
                            requiredMessage={messages['required_field']}
                        />
                    </div>
                    {
                        timeMetric?.id === "custom" &&
                        <div>
                            <InputDate
                                id="input-date-range"
                                type="range"
                                onChange={onChange}
                                value={startDate}
                                valueEnd={endDate}
                                maxDate={new Date()}
                            />
                        </div>
                    }
                </div>

                <TableView
                    ref={tableViewRef}
                    data={items || []}
                    total={total || []}
                    loading={loading}
                    page={page}
                    rowsPerPage={10}
                    topSpaceSize={440}
                    infiniteScroll={true}
                    viewType="list"
                    messages={messages}
                    serverSide
                    itemSelector={false}
                    onChangePage={(newPage) => {
                        onChangePage(newPage)
                        setPage(newPage)
                    }}
                    moreRowsAvailable={items?.length > total}
                    renderRow={(item) => {
                        return (
                            <CustomCard
                                customIcon={() => {
                                    return (<Icon style={{ color: 'black' }}>arrow_forward_ios</Icon>)
                                }}
                                customMessage={messages['maintenance_warnings_tab_details']}
                                onClick={() => {
                                    let dateRange = calculateDateRange()
                                    dispatch(openModalDetailedList({ unitId: data?.id, warning: item.warning, dateRange }))
                                }}
                                width={11}
                                minWithIcon={1}
                                styles={{ marginRight: 2, marginleft: 2 }}
                            >
                                <div style={{ display: 'grid', width: '100%' }}>
                                    <Tooltip title={!tab ? messages['maintenance_warnings_tab_resolve_issue'] : messages['maintenance_warnings_tab_clear']}>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            {
                                                !tab ? (
                                                    <IconStatus
                                                        firstIcon="check_circle_outline"
                                                        secondIcon="error"
                                                        onClick={() => {
                                                            let dateRange = calculateDateRange()
                                                            dispatch(openModalConfirmResolveMaintenanceWarningEvent({
                                                                unitId: data?.id,
                                                                unitLabel: data?.label,
                                                                dateRange: dateRange,
                                                                warning: item?.warning,
                                                                timezone: data?.timezone,
                                                                firstEventDate: item.firstEventDate,
                                                                lastEventDate: item.lastEventDate
                                                            }))
                                                        }}
                                                    />
                                                ) : (
                                                    <IconStatus
                                                        firstIcon="delete"
                                                        secondIcon="check_circle_outline"
                                                        onClick={() => {
                                                            let dateRange = calculateDateRange()
                                                            dispatch(openModalDeleteMaintenanceWarningEvent({ unitId: data?.id, unitLabel: data?.label, dateRange: dateRange, warning: item?.warning }))
                                                        }}
                                                    />
                                                )
                                            }
                                            <label
                                                onClick={() => {
                                                    if (!tab) {
                                                        let dateRange = calculateDateRange()
                                                        dispatch(openModalResolveMaintenanceWarningEvent({
                                                            unitId: data?.id,
                                                            unitLabel: data?.label,
                                                            dateRange: dateRange,
                                                            warning: item?.warning,
                                                            timezone: data?.timezone,
                                                            firstEventDate: item.firstEventDate,
                                                            lastEventDate: item.lastEventDate
                                                        }))
                                                    } else {
                                                        let dateRange = calculateDateRange()
                                                        dispatch(openModalDeleteMaintenanceWarningEvent({ unitId: data?.id, unitLabel: data?.label, dateRange: dateRange, warning: item?.warning }))
                                                    }
                                                }}
                                                style={{ fontWeight: 800, fontSize: 16, cursor: 'pointer' }}
                                            >
                                                {messages[`maintenance_warnings_tab_${item.warning}`] || item.warning}
                                            </label>
                                        </div>
                                    </Tooltip>
                                    <div>
                                        <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['maintenance_warnings_tab_first_occurrence']}:</label>
                                        <span>
                                            {item.firstEventDate ? getDateFormatted(item.firstEventDate, "date", "datetime", null, data?.timezone) : ''}
                                        </span>
                                    </div>
                                    <div>
                                        <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['maintenance_warnings_tab_last_occurrence']}:</label>
                                        <span>
                                            {item.lastEventDate ? getDateFormatted(item.lastEventDate, "date", "datetime", null, data?.timezone) : ''}{` (${timeFromDate(item.lastEventDate, locale)})`}
                                        </span>
                                    </div>
                                    {
                                        (tab && item.resolvedOn) ?
                                            <div>
                                                <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['maintenance_warnings_tab_resolved_on']}:</label>
                                                <span>
                                                    {getDateFormatted(item.resolvedOn, "date", "datetime", null, data?.timezone)}
                                                </span>
                                            </div>
                                            : <></>
                                    }
                                    {
                                        (tab && item.nameResolvedby) ?
                                            <div>
                                                <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['maintenance_warnings_tab_resolved_by']}:</label>
                                                <span>
                                                    {item.nameResolvedby}
                                                </span>
                                            </div>
                                            : <></>
                                    }
                                    <div
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => {
                                            let dateRange = calculateDateRange()
                                            dispatch(openModalDetailedList({ unitId: data?.id, warning: item.warning, dateRange }))
                                        }}
                                    >
                                        <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['maintenance_warnings_tab_warnings']}:</label>
                                        <Tooltip title={messages['maintenance_warnings_tab_details']}>
                                            <span>{item.eventCount}</span>
                                        </Tooltip>

                                    </div>
                                </div>
                            </CustomCard>
                        )
                    }}
                />
            </div>
        )
    }

    useEffect(() => {
        if (openSearch || openSearch === "") {
            const timer = setTimeout(() => {
                dispatch(reset({ items: [], total: 0, offset: 0 }))
                let dateRange = calculateDateRange()
                handleSearch({ unitId: data?.id, resolved: tab === 0 ? false : true, dateRange })
            }, 500)
            return () => clearTimeout(timer);
        }
    }, [openSearch])

    return (
        <>
            <Modal
                open={openModal}
                title={`${messages['maintenance_warnings_tab_title']} ${data?.label ? ` (${data.label})` : ''}`}
                position="right"
                size="small"
                handleClose={closeModal}
                loading={loading}
            >
                <ModalBody className="maintenance-warnings-modal-body">
                    <div style={{ padding: '0 10px' }}>
                        {
                            sdCard && sdCard?.status && sdCard?.status?.toUpperCase() !== "NORMAL" &&
                            <Alert
                                style={{ marginTop: '-10px', display: 'flex', alignItems: 'center', height: 50, fontSize: 11 }}
                                severity="error"
                                icon={<Icon style={{ color: '#F44336' }}>cancel</Icon>}
                            >
                                <div style={{ display: 'grid' }}>
                                    <span>{messages['maintenance_warnings_tab_sd_card_status']}  <strong>{sdCard?.status}</strong></span>
                                    <span>{messages['maintenance_warnings_tab_last_status']}:  <strong>{sdCard?.updatedOn ? getDateFormatted(sdCard?.updatedOn, "date", "datetime", null, data?.timezone) : ''}</strong></span>
                                </div>
                            </Alert>
                        }
                        <Tabs tab={tab} onChange={(event) => handleTabs(event)}>
                            <TabContainer index={0} label={messages['maintenance_warnings_tab_issues']}>
                                {generateCard()}
                            </TabContainer>
                            <TabContainer index={1} label={messages['maintenance_warnings_tab_resolved']}>
                                {generateCard()}
                            </TabContainer>
                        </Tabs>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button
                        className="btn btn-dark-blue"
                        onClick={() => closeModal()}
                    >
                        {messages['common_close']}
                    </Button>
                </ModalFooter>
            </Modal>
            {openModalViewDetailedList && <DetailedList />}
            {openModalViewResolve && <ResolveMaintenanceWarningsEvent />}
            {openModalConfirmRevolve && <ConfirmResolveMaintenanceWarningEvent />}
            {openModalDelete && <DeleteMaintenanceWarningEvent />}
        </>
    )
}

export default MaintenanceWarnings;